import React, {ReactNode} from 'react';
import { useState, useEffect, useRef } from 'react';
import headshot from './assets/headshot.png'
import mit from './assets/mit.png'
import ncaa from './assets/ncaa.png'
import cal from './assets/cal.png'
import caltech from './assets/caltech.png'
import sloan from './assets/sloan.png'
import idealab from './assets/idealab.png'
import amazon from './assets/amazon.png'
import driveline from './assets/driveline.png'
import facebook from './assets/facebook.png'
import scope from './assets/scope.png'
import ticketmaster from './assets/ticketmaster.png'
import MemoryIcon from '@mui/icons-material/Memory';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HubIcon from '@mui/icons-material/Hub';


import './App.css';

const useResponsiveSettings = () => {
  const [screenSize, setScreenSize] = useState<string>('desktop');

  useEffect(() => {
    const updateScreenSize = () => {
      if (window.innerWidth <= 950) {
        setScreenSize('mobile'); // mobile settings
      } else if (window.innerWidth <= 1400) {
        setScreenSize('tablet'); // tablet settings
      } else {
        setScreenSize('desktop'); // desktop settings
      }
    };

    // Initial check
    updateScreenSize();

    // Set up the resize listener
    window.addEventListener('resize', updateScreenSize);

    // Clean up the listener on unmount
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  return screenSize;
};

interface Particle {
  el: HTMLDivElement;
  life: number;
  vx: number;
  vy: number;
  x: number;
  y: number;
}

const MouseTrail: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const particles = useRef<Particle[]>([]);
  const gravity = -0.05;

  const screenSize = useResponsiveSettings()
  const isTablet = screenSize === 'tablet';
  const isMobile = screenSize === 'mobile';

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const createParticle = (x: number, y: number) => {
      const particle = document.createElement('div');
      particle.className = 'particle';

      const size = Math.random() * 6 + 8;
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;

      const vx = (Math.random() - 0.5) * 2; // -1 to 1
      const vy = (Math.random() - 0.5) * 2;

      container.appendChild(particle);
      particles.current.push({
        el: particle,
        life: 100,
        vx,
        vy,
        x,
        y,
      });
    };

    const handleMouseMove = (e: MouseEvent) => {
      const bounds = container.getBoundingClientRect();
      const x = e.clientX - bounds.left;
      const y = e.clientY - bounds.top;
      createParticle(x, y);
    };

    let animationId: number;

    const animate = () => {
      particles.current = particles.current.filter((p) => {
        p.life -= 1;

        // Apply gravity
        p.vy += gravity;

        // Update position
        p.x += p.vx;
        p.y += p.vy;

        // Apply styles
        p.el.style.left = `${p.x}px`;
        p.el.style.top = `${p.y}px`;
        p.el.style.opacity = (p.life / 60).toString();

        // Optional scale/shrink
        const scale = p.life / 60;
        p.el.style.transform = `scale(${scale})`;

        if (p.life <= 0) {
          p.el.remove();
          return false;
        }
        return true;
      });

      animationId = requestAnimationFrame(animate);
    };

    container.addEventListener('mousemove', handleMouseMove);
    animationId = requestAnimationFrame(animate);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
  <div id="spaces" ref={containerRef} className='trail-container' style={isMobile ? {padding: '60px 20px'} : {}}>
    <h2 className='text-xl'>
      Focusing on <span style={{fontWeight: 400}}>open-ended problems</span> that require
    </h2>
    <h2 className='text-xl'>
            <span
                style={{fontWeight: 700, color: 'black'}}>creative technical solutions</span>
    </h2>
    <br/>
    <h2 className='text-l'>
      I help partners bridge the widening gap between emerging technologies and meaningful real-world impact through a
      collaborative, iterative approach.
      Current projects include...
    </h2>
    <br/>
    <div className={isMobile ? 'col' : 'triple-component'}>
      <div className='column-component'>
        <h3 className='text-m-bold'>
          Sports Training, Recovery, and Psychology
        </h3>
        <h4 className='text-m'>
          Integrating cutting-edge research and technology into athletic routines and mindsets that translate onto the
          field.
        </h4>
      </div>
      {!isTablet &&
        <div className='column-component'>
          <h3 className='text-m-bold'>
            Education and Knowledge Transfer
          </h3>
          <h4 className='text-m'>
            Facilitating digital interactions between peers and multimodal content, using AI as a catalyst for
            human perspective.
          </h4>
        </div>
      }
      <div className='column-component'>
        <h3 className='text-m-bold'>
          Large-Scale Market Efficiency
        </h3>
        <h4 className='text-m'>
          Leveraging data and quant-trading methods to capture value in niche market opportunities, primarily in the
          sports sector.
        </h4>
      </div>
    </div>
    {isTablet &&
        <div className='column-component'>
          <h3 className='text-m-bold'>
            Education and Knowledge Transfer
          </h3>
          <h4 className='text-m'>
            Facilitating digital interactions between peers and multimodal content, using AI as a catalyst for
            human perspective.
          </h4>
        </div>
    }

  </div>
  )
      ;
};

function Carousel() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const carouselData = [
    {
      image: caltech,
      altText: "Caltech",
      size: '32px',
      title: "Research in natural language processing / streaming algorithms"
    },
    {image: sloan, altText: "Sloan", size: '48px', title: "Undergraduate research in the economics of startup growth" },
    { image: idealab, altText: "Idealab", size: '32px', title: "Simultaneous localization & mapping (SLAM) for autonomous scooters" },
    { image: amazon, altText: "Amazon", size: '32px', title: "Software development / internal frontend dashboards" },
    { image: driveline, altText: "Driveline", size: '32px', title: "Research in computer vision / automated ball-release detection" },
    { image: facebook, altText: "Facebook", size: '32px', title: "ML infra / Multimodal Artificial Intelligence Team (FAIM)" },
    { image: scope, altText: "Scope", size: '32px', title: "Product development and full-stack software engineering" },
    { image: ticketmaster, altText: "Ticketmaster", size: '32px', title: "Quant trading / market-making via 3rd-party broker access" }
  ];

  const allItems = [...carouselData, ...carouselData, ...carouselData];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationFrameId: any;
    let lastTime = 0;
    const pixelsPerSecond = 80;


    scrollContainer.scrollLeft = scrollContainer.scrollWidth / 3;

    const animate = (currentTime: any) => {
      if (lastTime === 0) {
        lastTime = currentTime;
      }

      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      const scrollAmount = (pixelsPerSecond * deltaTime) / 1000;
      scrollContainer.scrollLeft += scrollAmount;

      const oneThird = scrollContainer.scrollWidth / 3;
      if (scrollContainer.scrollLeft >= oneThird * 2) {
        scrollContainer.scrollLeft = oneThird;
      } else if (scrollContainer.scrollLeft < oneThird) {
        scrollContainer.scrollLeft = oneThird * 2 - 1;
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
      <div ref={scrollRef} id="collab" className='carousel-container'>
        {allItems.map((item, index) => (
            <div  key={index} className='carousel-component'>
                <img src={item.image} alt={item.altText} style={{height: item.size, marginBottom: item.size === '32px' ? '16px' : '0px'}} />
                <h4 className='text-s'>
                  {item.title}
                </h4>
            </div>
        ))}
      </div>
  )
}

function App() {
  const [isActiveMobileMenu, setIsActiveMobileMenu] = useState(false);
  const screenSize = useResponsiveSettings()
  const isTablet = screenSize === 'tablet';
  const isMobile = screenSize === 'mobile';
  return (
      <div id="home" className='app-container' style={{backgroundImage: 'url("/texture.png")',}}>
        <div style={{
          top: 0,
          left: 0,
          position: 'fixed',
          scrollbarWidth: 'none',
          right: 0,
          height: isActiveMobileMenu ? '100vh' : '0vh',
          zIndex: 99999,
          transition: "300ms ease-in-out",
          overflow: "scroll",
          backgroundColor: 'white',
          border: 'none'
        }}>
          <div style={{position: 'absolute', top: 30, right: 25}} onClick={() => setIsActiveMobileMenu(false)}>
            <CloseIcon onClick={() => setIsActiveMobileMenu(false)} style={{
              transform: (isActiveMobileMenu) ? 'rotate(0deg)' : 'rotate(-45deg)',
              opacity: (isActiveMobileMenu) ? 1 : 0,
              transition: '300ms ease-in-out',
              color: 'black',
              fontSize: '36px',
              cursor: 'pointer'
            }}/>

          </div>
          <div className='col' style={{marginTop: '150px'}}>
            <a href="#education" className="header-item" style={{fontSize: '28px', marginBottom: '48px'}} onClick={() => setIsActiveMobileMenu(false)}>EDUCATION / ATHLETICS</a>
            <a href="#collab" className="header-item" style={{fontSize: '28px', marginBottom: '48px'}} onClick={() => setIsActiveMobileMenu(false)}>COLLABORATION</a>
            <a href="#spaces" className="header-item" style={{fontSize: '28px', marginBottom: '48px'}} onClick={() => setIsActiveMobileMenu(false)}>SPACES / SKILLS</a>
            <a href="#connect" className="header-item" style={{fontSize: '28px', marginBottom: '48px'}} onClick={() => setIsActiveMobileMenu(false)}>CONNECT</a>
          </div>
        </div>
        <div className='top-section'>
          <div className='row'>
            <div className='left'>
              <a href="#home" className="header-logo">TH</a>
            </div>
            {!isMobile ?
                <>
                  <a href="#education" className="header-item">EDUCATION / ATHLETICS</a>
                  <a href="#collab" className="header-item">COLLABORATION</a>
                  <a href="#spaces" className="header-item">SPACES / SKILLS</a>
                  <a href="#connect" className="header-item">CONNECT</a>
                </>
                :
                <MenuIcon style={{fontSize: '32px', cursor: 'pointer'}} onClick={() => setIsActiveMobileMenu(true)}/>
              }

            </div>
            <img src={headshot} alt="Image" className='profile-pic'/>
            <h1 className='header-text'>
              TOMMY HANNAN
            </h1>
            <h2 className='subheader-text'>
              nyc / newport beach
            </h2>
            <br/>
            <div id="education" className={isMobile ? 'col' : 'triple-component'}>
              <div className='column-component'>
                <img src={mit} alt="Image" style={{height: '90px'}}/>
                <br/>
                <h3 className='text-s-bold'>
                  MASSACHUSSETTS INSTITUTE OF TECHNOLOGY
                </h3>
                <h4 className='text-s'>
                  B.S. in Computer Science - 4.9 / 5.0 GPA
                </h4>
                <h4 className='text-s'>
                  Grader for Design & Analysis of Algorithms
                </h4>
              </div>
              {!isTablet &&
                  <div className='column-component'>
                    <img src={ncaa} alt="Image" style={{height: '90px'}}/>
                    <br/>
                    <h3 className='text-s-bold'>
                      NCAA BASEBALL
                    </h3>
                    <h4 className='text-s'>
                      Division 1 Pitcher at UC Berkeley / 2022
                    </h4>
                    <h4 className='text-s'>
                      Division 3 Pitcher at MIT / 2018 - 2020
                    </h4>
                  </div>
              }
              <div className='column-component'>
                <img src={cal} alt="Image" style={{height: '90px'}}/>
                <br/>
                <h3 className='text-s-bold'>
                  UNIVERSITY OF CALIFORNIA, BERKELEY
                </h3>
                <h4 className='text-s'>
                  Graduate Programs in Business
                </h4>
                <h4 className='text-s'>
                  Administration and Entrepreneurship
                </h4>
              </div>
            </div>
            {isTablet &&
                <div className='column-component'>
                  <img src={ncaa} alt="Image" style={{height: '90px'}}/>
                  <br/>
                  <h3 className='text-s-bold'>
                    NCAA BASEBALL
                  </h3>
                  <h4 className='text-s'>
                    Division 1 Pitcher at UC Berkeley / 2022
                  </h4>
                  <h4 className='text-s'>
                    Division 3 Pitcher at MIT / 2018 - 2020
                  </h4>
                </div>
            }
          </div>
          {Carousel()}
          {<MouseTrail/>}

          <div className='skills-container' style={isMobile ? {padding: '10px 30px'} : {}}>
            <div className="col" style={{width: '100%'}}>
              <div className={isMobile ? 'col' : 'row'} style={{alignItems: isMobile ? 'center' : 'stretch', width: '100%'}}>
                <div className="skill-card">
                  <MemoryIcon className='skill-icon' style={{fontSize: '32px'}}/>
                  <h3 className='skill-text'>Multimodal AI/ML, Computer Vision, Natural Language Processing</h3>
                </div>
                <div className="skill-card">
                  <DashboardCustomizeIcon className='skill-icon' style={{fontSize: '32px'}}/>
                  <h3 className='skill-text'>Product and Interface Design (User and Business-Facing)</h3>
                </div>
              </div>
              <div className={isMobile ? 'col' : 'row'} style={{alignItems: isMobile ? 'center' : 'stretch', width: '100%'}}>
                <div className="skill-card">
                  <EmojiEventsIcon className='skill-icon' style={{fontSize: '32px'}}/>
                  <h3 className='skill-text'>Athlete Mindset, Competitive, Performance-driven</h3>
                </div>
                <div className="skill-card">
                  <HubIcon className='skill-icon' style={{fontSize: '32px'}}/>
                  <h3 className='skill-text'>Scalable Systems: Quant-Trading, Social Networks, Recommendations</h3>
                </div>
              </div>
            </div>
          </div>
        <div id="connect" className='bottom-section' style={isMobile ? {padding: '40px 40px'} : {}}>
          <h2 className='text-xl'>
            Have a cool project or idea? <span style={{fontWeight: 700}}>Let's talk</span>
          </h2>
          <br/>
          <h3 className='skill-text' style={{fontWeight: '600', fontSize: '20px'}}>
            (626) 720-3469
          </h3>
          <a className='connect-button' href="mailto:contact@tommyhannan.com?subject=Let's%20Talk">connect</a>
        </div>
      </div>
  );
}

export default App;
